import {
  customerReportSchema,
  dashboardSchema,
  financialReportSchema,
  productionReportSchema,
  salesReportSchema,
  staffProductivityReportSchema,
  type CustomerReport,
  type Dashboard,
  type FinancialReport,
  type PnlBasis,
  type ProductionReport,
  type ReportPreset,
  type SalesReport,
  type StaffProductivityReport,
} from '@tailoring-crm/shared';
import type { ApiClient } from '../api/client.js';

/**
 * Every dashboard and report call the app makes.
 *
 * The range travels as the PRESET the owner picked plus, only for `custom`, the two days —
 * never as a resolved pair of dates. The server resolves the preset against its own clock, so a
 * phone whose date is wrong (or whose timezone is not the shop's, which happens the week
 * somebody travels) cannot ask for the wrong week without meaning to. `custom` is the one case
 * where the days ARE the intent, and there they are sent as typed.
 *
 * Every CSV path is built from the same parameters as its screen, so the file is the export of
 * what is on the page rather than a second, similar question.
 */

const REPORTS = '/reports';

export interface RangeParams {
  preset: ReportPreset;
  from?: string | undefined;
  to?: string | undefined;
}

function rangeQuery(params: RangeParams): Record<string, string> {
  return {
    preset: params.preset,
    ...(params.preset === 'custom' && params.from ? { from: params.from } : {}),
    ...(params.preset === 'custom' && params.to ? { to: params.to } : {}),
  };
}

function csvPath(path: string, query: Record<string, string | number>): string {
  const search = new URLSearchParams(
    Object.entries({ ...query, format: 'csv' }).map(([key, value]) => [key, String(value)]),
  );
  return `${REPORTS}/${path}?${search.toString()}`;
}

// --- The dashboard ----------------------------------------------------------------------------

export async function getDashboard(
  client: ApiClient,
  options: { refresh?: boolean } = {},
): Promise<Dashboard> {
  return await client.get(`${REPORTS}/dashboard`, {
    schema: dashboardSchema,
    ...(options.refresh ? { query: { refresh: 'true' } } : {}),
  });
}

// --- The five reports --------------------------------------------------------------------------

export async function getSalesReport(
  client: ApiClient,
  params: RangeParams & { limit?: number },
): Promise<SalesReport> {
  return await client.get(`${REPORTS}/sales`, {
    schema: salesReportSchema,
    query: { ...rangeQuery(params), limit: params.limit ?? 10 },
  });
}

export function salesReportCsvPath(params: RangeParams & { limit?: number }): string {
  return csvPath('sales', { ...rangeQuery(params), limit: params.limit ?? 10 });
}

export async function getProductionReport(
  client: ApiClient,
  params: RangeParams,
): Promise<ProductionReport> {
  return await client.get(`${REPORTS}/production`, {
    schema: productionReportSchema,
    query: rangeQuery(params),
  });
}

export function productionReportCsvPath(params: RangeParams): string {
  return csvPath('production', rangeQuery(params));
}

export async function getStaffReport(
  client: ApiClient,
  params: RangeParams,
): Promise<StaffProductivityReport> {
  return await client.get(`${REPORTS}/staff`, {
    schema: staffProductivityReportSchema,
    query: rangeQuery(params),
  });
}

export function staffReportCsvPath(params: RangeParams): string {
  return csvPath('staff', rangeQuery(params));
}

export async function getCustomerReport(
  client: ApiClient,
  params: RangeParams & { limit?: number; lapsedDays?: number },
): Promise<CustomerReport> {
  return await client.get(`${REPORTS}/customers`, {
    schema: customerReportSchema,
    query: {
      ...rangeQuery(params),
      limit: params.limit ?? 10,
      lapsedDays: params.lapsedDays ?? 90,
    },
  });
}

export function customerReportCsvPath(
  params: RangeParams & { limit?: number; lapsedDays?: number },
): string {
  return csvPath('customers', {
    ...rangeQuery(params),
    limit: params.limit ?? 10,
    lapsedDays: params.lapsedDays ?? 90,
  });
}

export async function getFinancialReport(
  client: ApiClient,
  params: RangeParams & { basis: PnlBasis },
): Promise<FinancialReport> {
  return await client.get(`${REPORTS}/financial`, {
    schema: financialReportSchema,
    query: { ...rangeQuery(params), basis: params.basis },
  });
}

export function financialReportCsvPath(params: RangeParams & { basis: PnlBasis }): string {
  return csvPath('financial', { ...rangeQuery(params), basis: params.basis });
}
